import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, Save, Globe2, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CURRENCY_OPTIONS = [
  { code: "USD", name: "US Dollar" },
  { code: "CLP", name: "Chilean Peso" },
  { code: "EUR", name: "Euro" },
  { code: "DOP", name: "Dominican Peso" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "XCD", name: "East Caribbean Dollar" },
  { code: "TRY", name: "Turkish Lira" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "HTG", name: "Haitian Gourde" },
];

function fmtLocal(amount, code) {
  const noDec = code === "CLP" || code === "MXN" || code === "HTG";
  const n = Number(amount || 0);
  if (noDec) return `${Math.round(n).toLocaleString("en-US")} ${code}`;
  return `${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code}`;
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const [currency, setCurrency] = useState("USD");
  const [rate, setRate] = useState("1");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Platform timezone (MASTER_ADMIN only)
  const isAdmin = user?.role === "MASTER_ADMIN";
  const [tzList, setTzList] = useState([]);
  const [tz, setTz] = useState("UTC");
  const [savingTz, setSavingTz] = useState(false);

  useEffect(() => {
    api
      .get("/me/currency")
      .then((r) => {
        setCurrency(r.data.local_currency || "USD");
        setRate(String(r.data.exchange_rate ?? 1));
      })
      .finally(() => setLoading(false));
    if (isAdmin) {
      api.get("/app-settings/timezones").then((r) => {
        setTzList(r.data.timezones || []);
        setTz(r.data.current || "UTC");
      }).catch(() => {});
    }
  }, [isAdmin]);

  const saveTz = async () => {
    setSavingTz(true);
    try {
      await api.put("/admin/app-settings", { platform_timezone: tz });
      toast.success("Platform timezone updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingTz(false);
    }
  };

  const save = async () => {
    const r = parseFloat(rate);
    if (!r || r <= 0) {
      toast.error(t("settings.rate_invalid", "Exchange rate must be greater than 0"));
      return;
    }
    setSaving(true);
    try {
      await api.put("/me/currency", { local_currency: currency, exchange_rate: r });
      toast.success(t("settings.saved"));
      refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const rateNum = parseFloat(rate);
  const previewUsd = 1;
  const previewLocal = !isNaN(rateNum) ? previewUsd * rateNum : 0;
  const inputClass =
    "flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent";

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("nav.settings")}</div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {t("settings.title")}
        </h1>
        <p className="text-slate-500 mt-1 text-sm max-w-xl">
          {t("settings.subtitle")}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-2xl">
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">{t("common.loading")}</div>
        ) : (
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                {t("settings.currency")}
              </label>
              <select
                data-testid="settings-currency-select"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={inputClass}
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                {t("settings.exchange_rate")} &nbsp;
                <span className="text-slate-400 font-normal normal-case">(1 USD = X {currency})</span>
              </label>
              <input
                data-testid="settings-rate-input"
                type="number"
                step="0.0001"
                min="0"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className={inputClass}
                placeholder="e.g. 1300"
              />
            </div>

            <div className="rounded-lg border border-[#0F62FE]/20 bg-[#EDF5FF] px-4 py-3 flex items-center gap-3">
              <Globe2 className="w-5 h-5 text-[#0F62FE]" />
              <div className="text-sm">
                <div className="text-[#0F62FE] font-semibold">Customer-facing preview</div>
                <div className="text-slate-700">
                  A $1.00 USD topup will show on the receipt as{" "}
                  <span className="font-bold">{fmtLocal(previewLocal, currency)}</span>
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                data-testid="settings-save-btn"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t("common.save")}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="text-xs text-slate-500 max-w-2xl">
        Current account: <span className="font-semibold text-slate-700">{user?.name}</span> ({user?.role}).
        Changing currency only affects new topups going forward — past receipts keep the rate they were issued with.
      </div>

      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-2xl" data-testid="settings-tz-card">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-[#0F62FE]" />
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t("settings.platform_tz", "Platform timezone")}
            </h2>
          </div>
          <p className="text-slate-500 text-sm mb-4">
            {t(
              "settings.platform_tz_hint",
              "Official platform time used for all timestamps, scheduled jobs, weekly commission cutoffs, audit logs and reports."
            )}
          </p>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            Timezone
          </label>
          <select
            data-testid="settings-tz-select"
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent"
          >
            {tzList.map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
          <div className="mt-4">
            <button
              data-testid="settings-tz-save"
              onClick={saveTz}
              disabled={savingTz}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-50"
            >
              {savingTz ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t("common.save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
